import 'firebase/firestore';
import 'firebase/functions';
import {
  BASE_FORM_FIELDS,
  BulkSurveysWorkbookValidationResponse,
  RoomScheduleRowType,
} from './bulk-uploader-validation';

import { BigBatch } from '@qualdesk/firestore-big-batch';
import firebase from 'firebase/app';
import { UNSPECIFIED_LABEL } from '../../../../config/constants';
import { functions } from '../../../../config/store';
import { getLastUpdated } from '../../../../shared/logTools';
import { capitaliseString, getRandomID } from '../../../../shared/utilities';
import { BaseRoomScheduleRoomProps } from '../../../../types/room';
import { RoomSchedule } from '../../../../types/survey';
import { FLOOR_PLANS_CONFIG_DOC_ID } from '../../../../state/floorplans-config';
import { FORM_CONFIG_DOC_ID } from '../../../../state/form-config';
import { buildBulkSurveyPlan, BulkSurveyor } from './bulk-uploader-planning';
import { buildBulkFormFields } from './bulk-uploader-form-fields';
import {
  BulkSurveyIdentity,
  findExistingBulkSurveyDuplicates,
} from './bulk-uploader-existing-surveys';

const SURVEY_NAMES_DELIMITER = '***';
const YIELD_TIMEOUT_MS = 10;

// Get Firestore instance
const firestore = firebase.firestore();

export type BulkUpdateFeedback = {
  error?: boolean;
  status: string;
};

const documentCache = new Map<
  string,
  firebase.firestore.QueryDocumentSnapshot
>();

async function findUniqueDocument({
  collection,
  conditions,
  entityName,
}: {
  collection: firebase.firestore.Query;
  conditions: { field: string; value: string | number }[];
  entityName: string;
}): Promise<firebase.firestore.QueryDocumentSnapshot> {
  const cacheKey = `${entityName}-${JSON.stringify({
    collection: (collection as any)._query?.path.segments.join('/'),
    conditions,
  })}`;

  // Check if we have a cached result
  const cachedDocument = documentCache.get(cacheKey);
  if (cachedDocument) {
    return cachedDocument;
  }

  let query = collection.where('deleted', '==', 0);

  conditions.forEach(({ field, value }) => {
    query = query.where(field, '==', value);
  });

  const snapshot = await query.get();

  const conditionsString = conditions
    .map((c, index) => {
      const s = `${c.field}: ${c.value}`;
      return index === 0 ? s : ` and ${s}`;
    })
    .join('');

  if (snapshot.size > 1) {
    throw new Error(
      `More than one ${entityName} with ${conditionsString} exists. Unable to continue`,
    );
  } else if (snapshot.size === 0) {
    throw new Error(`No ${entityName} with ${conditionsString} exists`);
  }

  // Cache the result before returning
  documentCache.set(cacheKey, snapshot.docs[0]);

  console.log('document cache size increased:', documentCache.size);

  return snapshot.docs[0];
}
export async function assembleSurveys({
  clientId,
  workbookData,
  feedbackCallback,
}: {
  clientId: string;
  workbookData: BulkSurveysWorkbookValidationResponse;
  feedbackCallback: (feedback: BulkUpdateFeedback) => void;
}) {
  let hasErrors = false;

  function logError(message: string) {
    hasErrors = true;
    feedbackCallback({ status: message, error: true });
  }

  function logInfo(message: string) {
    feedbackCallback({ status: message });
  }

  try {
    const clientDocumentRef = firestore.collection('clients').doc(clientId);

    const clientDoc = await clientDocumentRef.get();

    if (clientDoc.exists === false) {
      throw feedbackCallback({
        status: `Client ${clientId} not found`,
      });
    }
    const surveySetup = workbookData.Setup.data;

    logInfo('Checking for surveys that already exist...');

    const surveyIdentities: BulkSurveyIdentity[] = [];
    for (const survey of surveySetup) {
      const siteDoc = await findUniqueDocument({
        collection: clientDocumentRef.collection('sites'),
        conditions: [
          { field: 'name', value: capitaliseString(survey['Site Name']) },
        ],
        entityName: 'site',
      });
      const buildingDoc = await findUniqueDocument({
        collection: clientDocumentRef
          .collection('sites')
          .doc(siteDoc.id)
          .collection('buildings'),
        conditions: [
          { field: 'name', value: capitaliseString(survey['Building Name']) },
        ],
        entityName: 'building',
      });

      surveyIdentities.push({
        name: survey['Survey Name'],
        siteId: siteDoc.id,
        siteName: siteDoc.data().name,
        buildingId: buildingDoc.id,
        buildingName: buildingDoc.data().name,
      });
    }

    const duplicateSurveys = await findExistingBulkSurveyDuplicates(
      clientDocumentRef.collection('surveys'),
      surveyIdentities,
    );
    if (duplicateSurveys.length) {
      throw new Error(
        `Surveys already exist: ${duplicateSurveys
          .map(
            ({ name, siteName, buildingName }) =>
              `${name} - ${siteName} - ${buildingName}`,
          )
          .join(', ')}. No surveys were created.`,
      );
    }

    logInfo(`Processing ${surveySetup.length} surveys...`);

    const BATCH_SIZE = 50; // Process 50 surveys per Firestore batch to avoid 10MB limit
    const importPromises: (() => Promise<firebase.functions.HttpsCallableResult>)[] =
      [];

    // Process surveys in batches to avoid Firestore batch size limits
    for (
      let batchStart = 0;
      batchStart < surveySetup.length;
      batchStart += BATCH_SIZE
    ) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, surveySetup.length);
      const surveyBatch = surveySetup.slice(batchStart, batchEnd);

      logInfo(
        `Processing batch: surveys ${batchStart + 1}-${batchEnd} of ${
          surveySetup.length
        }`,
      );

      const batch = new BigBatch({ firestore });

      for (let i = 0; i < surveyBatch.length; i++) {
        const survey = surveyBatch[i];
        const globalIndex = batchStart + i;

        // Yield to browser every 10 surveys to keep UI responsive
        if (globalIndex > 0 && globalIndex % 10 === 0) {
          await new Promise((resolve) => setTimeout(resolve, 0));
          logInfo(
            `Progress: ${globalIndex}/${surveySetup.length} surveys processed`,
          );
        }

        logInfo(
          `Creating Survey: ${survey['Survey Name']} (${globalIndex + 1}/${
            surveySetup.length
          })`,
        );

        let surveyorData: BulkSurveyor = {
          nameFirst: 'Not',
          nameLast: 'Assigned',
          uid: UNSPECIFIED_LABEL,
          email: UNSPECIFIED_LABEL,
        };

        if (survey['Surveyor Email'] === undefined) {
          logInfo(`Setting survey as unnassigned`);
        } else {
          // Find surveyor
          const surveyorDoc = await findUniqueDocument({
            collection: firestore.collection('roles'),
            conditions: [
              { field: 'email', value: survey['Surveyor Email'] },
              { field: 'clientId', value: clientDoc.id },
            ],
            entityName: 'surveyor',
          });

          surveyorData = surveyorDoc.data() as BulkSurveyor;

          logInfo(
            `Assigning to surveyor: ${surveyorDoc.data().nameFirst} ${
              surveyorDoc.data().nameLast
            } with email ${survey['Surveyor Email']}`,
          );
        }

        // Find taxonomy
        const assetListDoc = await findUniqueDocument({
          collection: clientDocumentRef.collection('assetLists'),
          conditions: [{ field: 'name', value: survey['Taxonomy'] }],
          entityName: 'taxonomy',
        });

        logInfo(
          `Using taxonomy: ${survey['Taxonomy']} with id ${assetListDoc.id}`,
        );

        // Find site
        const siteDoc = await findUniqueDocument({
          collection: clientDocumentRef.collection('sites'),
          conditions: [
            { field: 'name', value: capitaliseString(survey['Site Name']) },
          ],
          entityName: 'site',
        });

        logInfo(`Using site: ${survey['Site Name']} with id ${siteDoc.id}`);

        // Find building
        const buildingDoc = await findUniqueDocument({
          collection: clientDocumentRef
            .collection('sites')
            .doc(siteDoc.id)
            .collection('buildings'),
          conditions: [
            { field: 'name', value: capitaliseString(survey['Building Name']) },
          ],
          entityName: 'building',
        });

        logInfo(
          `Using building: ${survey['Building Name']} with id ${buildingDoc.id}`,
        );

        const formFieldsForThisSurvey = workbookData['Form Fields'].data.filter(
          (f) => {
            const surveyNames = f['Survey Names']
              ?.split(SURVEY_NAMES_DELIMITER)
              .map((s) => {
                return s.trim();
              });
            const isBaseField = BASE_FORM_FIELDS.includes(f.Label);

            if (!surveyNames || isBaseField) {
              return true;
            }
            return surveyNames.includes(survey['Survey Name']);
          },
        );

        feedbackCallback({
          status: `Assembling ${formFieldsForThisSurvey.length} form fields`,
        });

        const formFields = buildBulkFormFields(formFieldsForThisSurvey);

        const roomsForSurveySiteAndBuilding = workbookData[
          'Room Schedule'
        ]?.data?.filter((r) => {
          const surveyNames = r['Survey Names']
            ?.split(SURVEY_NAMES_DELIMITER)
            .map((s) => {
              return s.trim();
            }) ?? [survey['Survey Name']];
          return (
            surveyNames.includes(survey['Survey Name']) &&
            r['Site Name'] === survey['Site Name'] &&
            r['Building Name'] === survey['Building Name']
          );
        });

        const roomSchedule = {
          data: roomsForSurveySiteAndBuilding?.map(generateRoom),
          floors:
            Array.from(
              new Set(roomsForSurveySiteAndBuilding?.map((r) => r.Floor)),
            ) ?? [],
          fileName: '',
          lastModified: 0,
          url: '',
        };

        const newSurveyDoc = clientDocumentRef.collection('surveys').doc();

        const importsForThisSurvey =
          workbookData.Imports?.data.filter((i) => {
            const surveyNames = i['Survey Names']
              ?.split(SURVEY_NAMES_DELIMITER)
              .map((s) => {
                return s.trim();
              }) ?? [survey['Survey Name']];

            return surveyNames.includes(survey['Survey Name']);
          }) ?? [];

        const roomsFromImports: RoomSchedule['data'] = [];
        importsForThisSurvey.forEach((i) => {
          const roomName = i['Room Name'];
          const room = roomSchedule.data.find(
            (r) => r.name === roomName && r.floor === i['Floor'],
          );
          if (!room) {
            const importedRoom = {
              id: getRandomID(),
              name: roomName,
              floor: i['Floor'] ?? '',
              areaFloor: i['Room Area (floor)'] ?? 0,
              areaWall: i['Room Area (wall)'] ?? 0,
              deleted: 0,
              imported: true,
            };
            roomsFromImports.push(importedRoom);
          }
        });

        const finalRoomSchedule = {
          ...roomSchedule,
          data:
            // remove possible duplicates
            [...roomSchedule.data, ...roomsFromImports].filter(
              (item, index, self) =>
                index ===
                self.findIndex(
                  (t) => t.name === item.name && t.floor === item.floor,
                ),
            ) ?? [],
        };

        finalRoomSchedule?.data?.length &&
          logInfo(
            `Inserting ${finalRoomSchedule.data.length} rooms into room schedule`,
          );

        logInfo(`Saving survey to database`);

        const surveyDocRef = clientDocumentRef
          .collection('surveys')
          .doc(newSurveyDoc.id);
        const { lastUpdated } = getLastUpdated();
        const plan = buildBulkSurveyPlan({
          surveyId: newSurveyDoc.id,
          client: {
            id: clientDoc.id,
            name: clientDoc.data()?.name ?? '',
            orgId: clientDoc.data()?.orgId ?? null,
            orgName: clientDoc.data()?.orgName ?? null,
          },
          assetListId: assetListDoc.id,
          site: {
            id: siteDoc.id,
            name: siteDoc.data().name,
          },
          building: {
            id: buildingDoc.id,
            name: buildingDoc.data().name,
          },
          surveyor: surveyorData,
          setup: survey,
          formFields,
          roomSchedule: finalRoomSchedule,
          imports: importsForThisSurvey,
          created: Date.now(),
          lastUpdated,
        });

        batch.set(surveyDocRef, plan.survey);
        batch.set(
          surveyDocRef.collection('config').doc(FORM_CONFIG_DOC_ID),
          plan.formConfig,
          { merge: true },
        );
        batch.set(
          surveyDocRef.collection('config').doc(FLOOR_PLANS_CONFIG_DOC_ID),
          plan.floorPlansConfig,
        );
        plan.roomDocuments.forEach((roomDocument) => {
          batch.set(
            surveyDocRef.collection('rooms').doc(roomDocument.id),
            roomDocument,
            { merge: true },
          );
        });

        workbookData.Imports?.data.length &&
          feedbackCallback({
            status: `Beginning import of ${importsForThisSurvey.length} assets`,
          });

        // Only pass the import promise if there are imports for this survey
        if (plan.importPayload) {
          const importPromise = () => {
            return functions.httpsCallable('importAssetData')(
              plan.importPayload,
            );
          };

          importPromises.push(importPromise);
        }

        logInfo(`Survey ${survey['Survey Name']} created successfully`);

        logInfo('');
      }

      // Commit this batch of surveys
      logInfo(`Saving batch ${batchStart + 1}-${batchEnd} to database...`);
      await batch.commit();
      logInfo(`Batch ${batchStart + 1}-${batchEnd} saved successfully!`);

      // Yield to browser after committing each batch
      await new Promise((resolve) => setTimeout(resolve, YIELD_TIMEOUT_MS));
    }

    logInfo('All surveys saved successfully!');

    // Process imports in batches to avoid overwhelming the system
    if (importPromises.length > 0) {
      const IMPORT_BATCH_SIZE = 5; // Process 5 imports at a time
      logInfo(
        `Starting import of assets for ${importPromises.length} surveys...`,
      );

      for (let i = 0; i < importPromises.length; i += IMPORT_BATCH_SIZE) {
        const batchPromises = importPromises
          .slice(i, i + IMPORT_BATCH_SIZE)
          .map((fn) => fn());

        await Promise.all(batchPromises);

        const processed = Math.min(
          i + IMPORT_BATCH_SIZE,
          importPromises.length,
        );
        logInfo(
          `Import progress: ${processed}/${importPromises.length} surveys processed`,
        );

        // Yield to browser to keep UI responsive
        await new Promise((resolve) => setTimeout(resolve, YIELD_TIMEOUT_MS));
      }

      logInfo('All imports completed!');
    }
  } catch (error) {
    if (error instanceof Error) {
      logError(`Error: ${error.message}`);
      throw error;
    } else {
      logError(`An unknown error occurred`);
    }
  } finally {
    hasErrors
      ? logError(
          'Finished bulk import with errors, no surveys have been created. Please fix them and try again.',
        )
      : logInfo(
          'Finished bulk import successfully! Depending on how many surveys you created, they may take a while to show in the surveys list.',
        );
  }
}

function generateRoom(room: RoomScheduleRowType): BaseRoomScheduleRoomProps {
  return {
    id: getRandomID(),
    name: room['Room Name'],
    floor: room.Floor,
    areaFloor: room['Room Area (floor)'] ?? 0,
    areaWall: room['Room Area (wall)'] ?? 0,
    deleted: 0,
    notes: room.Notes ?? '',
    imported: true,
  };
}
